/* 151F §2 — THE SELECT WHOSE OPEN FACE IS THE APP'S OWN MENU.
 *
 * The OS paints a native <select>'s menu (black under macOS dark mode) and the
 * app cannot style it. RULED: the dropdowns the glance met become app
 * dropdowns — the 151D autocomplete/popover idiom, app colors, scroll discs —
 * and "reuse ONE idiom; do not invent a second popover". The census of what
 * this tree already has, and why each existing face cannot simply be mounted:
 *
 *   TypeaheadPicker.tsx   the 151D idiom itself — but its trigger is an INPUT
 *                         with completion machinery (matchesFor, InputEvent
 *                         verbs, a lifted query contract). A select has no
 *                         text to complete; wedging "no typing" into it would
 *                         fork every arm of that machinery.
 *   FieldSelect.tsx       the ONE native select (05m-b) — native menu is the
 *                         thing being ruled out here; its searchable arm
 *                         (FieldCombo) SORTS alphabetically (sortOptions —
 *                         right for 249 countries, wrong for the ruled lists,
 *                         whose seed order is deliberate and divider-grouped)
 *                         and wears its own fs-combo-* dress, not the 151D
 *                         menu's.
 *
 * So this is the select FACE over the SAME popover: useAnchoredMenu (the one
 * extracted placement, 134 §1), TypeaheadPicker's own MENU_BOX/BACKDROP dress
 * (imported, not copied), the modals' ScrollHint discs (151D §1(b)+(e)'s
 * wiring, verbatim), FieldCombo's keyboard march and its 108 §6b divider row.
 * Nothing here positions, paints or dismisses in a new way.
 *
 * SCOPE (151F §2): the document modal's Type + Status selects and the
 * time-entry period select. The app-wide <select> conversion is B.3 element
 * work — Laurent rules when it runs; this component is what that sweep would
 * mount.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { FS, Z } from "../lib/tokens";
import { useMenuLevel } from "../lib/paneStacking"; // 204 §1
import { useAnchoredMenu } from "../hooks/useAnchoredMenu";
import { ScrollHint, useScrollHint } from "./SharedUI";
import { MENU_BOX, BACKDROP, MENU_MIN_W } from "./TypeaheadPicker";
import { isDividerCode } from "../utils/taxonomyMap";

/* 154B §1 — AN OPTION MAY CARRY A TOOLTIP, and that is the whole widening.
   A native <option title="…"> is what FieldSelect's provisional row uses to
   explain itself ("this entry no longer exists — pick a current one to relink
   it"), and a custom menu that dropped it would drop the sentence that tells a
   user what to DO about a marked value. Kept as a third tuple slot rather than
   a `mark` prop, because this component must not learn what a taxonomy is: it
   learns that a row can have a tooltip. */
export type MenuSelectOption =
  | readonly [string, string]
  | readonly [string, string, string];

type Props = {
  /** what the record holds; "" is a real, pickable state when the caller
   *  includes an ["", label] option (the doc Type's "—", the period's
   *  "All dates") */
  value: string;
  onChange: (v: string) => void;
  /** [code, label] or [code, label, tooltip] in the CALLER'S order — never
   *  sorted here (the ruled lists' seed order is deliberate); divider codes
   *  render as lines */
  options: ReadonlyArray<MenuSelectOption>;
  /** 154B §1 — the bar select's `disabled={!_coAdm}`, arriving through
   *  FieldSelect. The REAL attribute and not aria-disabled: 151G's ruling
   *  against it is about a control whose hover text must still show, and this
   *  trigger carries none — a caller passing BOTH `title` and `disabled` would
   *  hit that WebKit behaviour, which is why they are documented together. */
  disabled?: boolean;
  /** the trigger's dress — "field-input" in the modal; the period filter
   *  passes its shell's own paint-nothing style instead */
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  /** 154B §2 — InvitationPolicyPanel's witness select carries `id`, and the
   *  <label htmlFor> above it is the only thing naming the control. A <button>
   *  is a labelable element, so the association survives the conversion. */
  id?: string;
  title?: string;
  /** the built-in caret; the period trigger already has a ChevronDown
   *  sibling in its shell and passes false */
  caret?: boolean;
  /** a native select's box is as wide as its WIDEST option; a button's is as
   *  wide as the CURRENT label, so a paint-nothing host whose shell
   *  auto-sizes (the te period filter's .teActBox) would change footprint on
   *  every pick. This renders every option label into the same grid cell,
   *  hidden — the native rule, reproduced without a px literal. */
  intrinsicWidth?: boolean;
  /** the menu must clear the surface that hosts the trigger — the document
   *  modal sits at 300 and passes 301; default is the idiom's own 201
   *  (clears .modal-overlay at 100). 204 §1: inside a ModalLayout LIFTED over
   *  an open pane the menu takes one over the lifted shell instead
   *  (`useMenuLevel`), whatever figure is passed — a 201 or a 301 would paint
   *  under the 10001 overlay. */
  zIndex?: number;
  /* ── 154B §2 — THE THREE THINGS AN INLINE ROW EDITOR DID WITH THE DOM ─────
   *
   * CaseTaskRow, CaseEventRow and SubtaskChecklist mount their editor ON the
   * cell's click, and they made a native <select> behave like an editor with a
   * ref callback (`focus()` then `showPicker()`), an `autoFocus`, and an
   * `onBlur` that leaves edit mode. None of the three survives a conversion by
   * accident: a native picker cannot be opened by a prop, and a menu that
   * portals its rows moves focus off the trigger the moment it is used.
   */
  /** focus the trigger AND open the menu on mount — one click on the cell both
   *  enters edit and opens the list, which is what the ref callback bought. */
  autoOpen?: boolean;
  /** focus the trigger on mount without opening it. */
  autoFocus?: boolean;
  /** focus has LEFT the control. Not the button's raw blur: picking a row
   *  blurs the trigger before the click lands, and a pick that closes the
   *  editor unmounts the trigger outright. Both are "still inside the
   *  control", so the answer is deferred and gated on the menu being shut. */
  onBlur?: () => void;
  /** 154B §2 — per-ROW style, for the one site where the option's appearance
   *  IS the information: Settings' app-font picker gave every <option> its own
   *  fontFamily, so the list shows each face in itself. THE FOUR RULES 4 —
   *  widen the primitive rather than bend the behaviour. A function of the
   *  value, so this component never learns what a font is; the CLOSED face is
   *  untouched, exactly as a native select's was (an <option style> paints the
   *  row in the popup, never the box). */
  optionStyle?: (value: string) => CSSProperties | undefined;
  /** 197B §A.2(4) — THE ROWS THAT CANNOT BE PICKED, for the one site where an
   *  option's PRESENCE is the information and its pickability is not: the
   *  e-mails drawer's « Limiter à » lists every period and marks the ones the
   *  search did not cover « Hors recherche », so the reader sees the whole
   *  list and learns which a wider search would unlock — a list that hid
   *  them could not say why. A set of values, so this component never learns
   *  what a period is (a predicate would be `optionStyle`'s shape, and a
   *  function type with a named parameter is a warning the ratchet counts).
   *  Such a row wears `aria-disabled`, the muted ink and no pointer; `pick()`
   *  refuses it exactly as it refuses a divider (the keyboard march may pass
   *  over but never commit; type-ahead skips it). Never the native
   *  `disabled` — a `<div role="option">` has none — and never a hidden row. */
  unpickable?: ReadonlySet<string>;
};

export default function MenuSelect({
  value,
  onChange,
  options,
  disabled,
  className,
  style,
  ariaLabel,
  id,
  title,
  caret = true,
  intrinsicWidth = false,
  zIndex = Z.popover,
  autoOpen = false,
  autoFocus = false,
  onBlur,
  optionStyle,
  unpickable,
}: Props) {
  const [open, setOpenState] = useState(false);
  const [active, setActive] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /* 154B §2 — the same fact in a ref, because the blur check below runs from a
     deferred task and a captured `open` would answer for the render it was
     captured in, not for the moment it is asked. */
  const openRef = useRef(false);
  const setOpen = (v: boolean) => {
    openRef.current = v;
    setOpenState(v);
  };
  const blurRef = useRef(onBlur);
  blurRef.current = onBlur;
  /* Deferred, and gated on the menu being shut. Three states have to read as
     "focus is still inside": a mousedown on a row (blur fires before the click
     that picks), a pick that unmounts the trigger (the caller left edit mode
     itself), and Escape (which puts focus back). Everything else is a real
     departure and closes the editor exactly as the <select>'s blur did. */
  const checkBlur = () => {
    if (!blurRef.current) return;
    setTimeout(() => {
      if (openRef.current) return;
      const el = triggerRef.current;
      if (!el || el.ownerDocument.activeElement === el) return;
      blurRef.current?.();
    }, 0);
  };

  /* the closed face: the matching option's label; an unmatched stored value
     shows ITSELF rather than falling to the first option — the truthful
     fraction of FieldSelect's provisional rule (a native select silently
     displays the first option in that state; this face must not lie better) */
  const current = options.find(([v]) => v === value);
  const closedText = current ? current[1] : value;

  const menuHint = useScrollHint(open);
  /* 204 §1 — the level the menu paints at: the prop, or one over a lifted host. */
  const menuZ = useMenuLevel(zIndex);

  useAnchoredMenu({
    open,
    anchorRef: triggerRef,
    menuRef,
    onDismiss: () => {
      setOpen(false);
      checkBlur();
    },
    deps: [options.length],
    minWidth: MENU_MIN_W,
  });

  const openMenu = () => {
    const i = options.findIndex(([v]) => v === value);
    setActive(i >= 0 ? i : 0);
    setOpen(true);
  };
  /* 154B §2 — closed BY the control (a pick, an Escape) puts focus back on the
     trigger, which is what a native select does and what keeps the deferred
     blur check from reading a pick as a departure. A backdrop dismiss does
     not: there the user clicked somewhere else and focus belongs there. */
  const closeKeepingFocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const pick = (v: string) => {
    if (isDividerCode(v)) return; // 108 §6b — a divider is never a value
    if (unpickable?.has(v)) return; // 197B §A.2(4) — an unpickable row is never a value either
    onChange(v);
    closeKeepingFocus();
  };

  /* Mount only: these rows are mounted BY the click that enters edit, so
     "on mount" is "on that click" and the transient activation the old
     showPicker() needed is not a question this menu has to ask. */
  useEffect(() => {
    if (!autoOpen && !autoFocus) return;
    const el = triggerRef.current;
    if (!el) return;
    el.focus();
    if (autoOpen) openMenu();
    /* `[]` is the point, not an omission: these editors are MOUNTED by the
       click that enters edit and unmounted when it leaves, so "on mount" is
       the event, and a dep that re-ran this would reopen the list mid-pick —
       which is the exact bug the ref callback it replaces carried a useCallback
       to avoid. No suppression comment: this repo runs no ESLint, so one would
       be decoration the `any` ratchet counts. */
  }, []);

  /* 154B §1 — TYPE-AHEAD, because a native select has it and this face replaces
     one. Typing letters jumps to the first option whose LABEL starts with what
     has been typed so far; the buffer resets after a pause, so "be" finds
     Belgium and a later "l" starts again rather than searching "bel…l". Two
     behaviours, both the native ones: with the menu OPEN it moves the
     highlight, and with the menu CLOSED it commits the value outright — which
     is the one fast data entry actually uses.

     Space is deliberately NOT a type-ahead character: on a button it is the
     activation key, and a native select opens on it too. */
  const typeBuf = useRef({ s: "", at: 0 });
  const typeAhead = (ch: string) => {
    const now = Date.now();
    const buf = now - typeBuf.current.at > 700 ? ch : typeBuf.current.s + ch;
    typeBuf.current = { s: buf, at: now };
    const q = buf.toLowerCase();
    const i = options.findIndex(
      ([v, l]) => !isDividerCode(v) && !unpickable?.has(v) && String(l).toLowerCase().startsWith(q),
    );
    if (i < 0) return false;
    if (open) setActive(i);
    else pick(options[i][0]);
    return true;
  };

  /* The highlighted row must stay visible, or the arrow keys walk off the
     bottom of a scrolled menu with nothing to show for it — the one thing a
     native select does for free that a portalled div does not. `nearest` so it
     never re-centres a row that is already on screen. */
  useEffect(() => {
    if (!open) return;
    const el = menuRef.current?.querySelector<HTMLElement>(
      `[data-menusel-opt][data-i="${active}"]`,
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={className}
        /* a button centres its text; a select face reads left like the field
           it replaces, and the caret rides the right edge of the same box */
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          textAlign: "left",
          cursor: "pointer",
          ...(style || {}),
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onBlur={checkBlur}
        onKeyDown={(e) => {
          if (
            e.key.length === 1 &&
            e.key !== " " &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey
          ) {
            if (typeAhead(e.key)) e.preventDefault();
            return;
          }
          if (!open) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              openMenu();
            }
            return;
          }
          if (e.key === "Escape") {
            /* the menu eats its own Escape — the host modal's escToClose
               window listener must not fire on the SAME press */
            e.stopPropagation();
            closeKeepingFocus();
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, options.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const hit = options[active];
            if (hit) pick(hit[0]);
          }
        }}
      >
        {intrinsicWidth ? (
          <span style={{ display: "grid", flex: 1, minWidth: 0 }}>
            <span
              style={{
                gridArea: "1 / 1",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {closedText}
            </span>
            {options
              .filter(([v]) => !isDividerCode(v))
              .map(([v, l]) => (
                <span
                  key={v}
                  aria-hidden
                  style={{
                    gridArea: "1 / 1",
                    visibility: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {l}
                </span>
              ))}
          </span>
        ) : (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {closedText}
          </span>
        )}
        {caret && (
          <span
            aria-hidden
            style={{
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "5px solid var(--tm)",
              flexShrink: 0,
            }}
          />
        )}
      </button>
      {open &&
        createPortal(
          <>
            <div
              data-menusel-backdrop
              style={{ ...BACKDROP, zIndex: menuZ - 1 }}
              onMouseDown={() => {
                setOpen(false);
                checkBlur();
              }}
            />
            <div
              data-menusel
              ref={(el) => {
                menuRef.current = el;
                menuHint.ref.current = el;
              }}
              role="listbox"
              style={{ ...MENU_BOX, zIndex: menuZ }}
            >
              {menuHint.above && <ScrollHint dir="up" />}
              {options.map((opt, i) => {
                const [v, l] = opt;
                const rowTitle = opt[2];
                const off = !!unpickable?.has(v); // 197B §A.2(4)
                return isDividerCode(v) ? (
                  /* 108 §6b — the custom menu draws a rule; pick() refuses its
                     code, so the keyboard march may pass over but never commit */
                  <div
                    key={v}
                    aria-hidden
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 10px",
                      color: "var(--tm)",
                      fontSize: FS.s9,
                      pointerEvents: "none",
                    }}
                  >
                    <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
                    {l ? <span>{l}</span> : null}
                    <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
                  </div>
                ) : (
                  <div
                    key={v}
                    data-menusel-opt
                    data-i={i}
                    title={rowTitle}
                    role="option"
                    aria-selected={v === value}
                    aria-disabled={off || undefined}
                    style={{
                      padding: "6px 10px",
                      cursor: off ? "default" : "pointer",
                      fontSize: FS.s10_5,
                      background: i === active ? "var(--bh)" : "none",
                      /* the chosen row says so in weight, not colour — every
                         theme keeps its own row text; an unpickable row in
                         the muted ink (197B §A.2(4)) */
                      fontWeight: v === value && v !== "" ? 600 : 400,
                      ...(off ? { color: "var(--tm)" } : null),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      ...(optionStyle ? optionStyle(v) : null),
                    }}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(v)}
                  >
                    {l}
                  </div>
                );
              })}
              {menuHint.below && <ScrollHint dir="down" />}
            </div>
          </>,
          (triggerRef.current && triggerRef.current.ownerDocument.body) ||
            document.body,
        )}
    </>
  );
}
